import React, { useEffect, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { Auth, LoadingComponent } from 'recupero-comunes';
import {
    actualizarIngreso, existeCaso, getCausales, getCiudades, getColores, getComunas, getCompanias, getConfiguracion, getMarcas,
    getResponsables, getTiposSiniestro, getTiposVehiculo, getUrlCarta, getUsos, obtenerCaso, validarIngreso, crearIngreso
} from '../../api/ingreso-manual-client';
import { MODO_DEFENSA, MODO_RECUPERO, TIPOS_SINIESTRO_PERMITIDOS } from '../../common/config-ingreso-manual';
import { construyePayload, erroresAMapa, leeArchivoBase64, valoresDeCaso, valoresIniciales } from '../../common/ingreso-manual';
import Formulario from './formulario';
import ModalExistente from './modal-existente';
import ResumenModal from './resumen-modal';
import Resultado from './resultado';

const SIN_EXISTENTE = { show: false, idCaso: null, cargando: false, error: '' };
const SIN_REVISION = { show: false, valores: null, error: '' };

const NOMBRE_MODO = { [MODO_RECUPERO]: 'Recupero directo', [MODO_DEFENSA]: 'Cartas inter cía (defensa entre compañías)' };

// Ingreso manual de casos Recupero Inter Cía: el caso queda en la bandeja del abogado responsable, desde donde él
// lo deriva a Inter Cía. Flujo: formulario → "Revisar" (valida en el servidor) → modal de revisión → "Confirmar".
// RG-591: el modo (recupero / cartas inter cía) lo define el rol del usuario en el servidor; si al escribir siniestro y patente ya
// existe un caso, se ofrece actualizarlo (modo edición) o seguir con un ingreso nuevo.
const IngresoManualInterCia = () => {

    const { rutUsuario, rutOrganizacion } = Auth.currentUser();
    const usuario = { rutUsuario, rutOrganizacion };

    const [catalogos, setCatalogos] = useState(null);
    const [modos, setModos] = useState([MODO_RECUPERO]);
    const [modo, setModo] = useState(MODO_RECUPERO);
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState('');
    const [errorGeneral, setErrorGeneral] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [revision, setRevision] = useState(SIN_REVISION);
    const [resultado, setResultado] = useState(null);
    const [claveFormulario, setClaveFormulario] = useState(0);
    // Modo edición: { idCaso } mientras se actualiza un caso existente; valoresForm son los que arrancan el formulario.
    const [edicion, setEdicion] = useState(null);
    const [valoresForm, setValoresForm] = useState(valoresIniciales());
    const [existente, setExistente] = useState(SIN_EXISTENTE);

    useEffect(() => {
        const cargaCatalogos = async () => {
            setCargando(true);
            setErrorCarga('');
            try {
                const [tiposSiniestro, tiposVehiculo, marcas, comunas, ciudades, companias, responsables, causales, colores, usos, configuracion] = await Promise.all([
                    getTiposSiniestro(), getTiposVehiculo(), getMarcas(rutOrganizacion),
                    getComunas(rutOrganizacion), getCiudades(rutOrganizacion), getCompanias(), getResponsables(rutOrganizacion),
                    getCausales(), getColores(), getUsos(), getConfiguracion()
                ]);
                setCatalogos({
                    // Solo Pérdida Total y Pérdida Parcial.
                    tiposSiniestro: tiposSiniestro.filter((t) => TIPOS_SINIESTRO_PERMITIDOS.includes(String(t.id))),
                    tiposVehiculo,
                    marcas,
                    comunas,
                    ciudades,
                    // La compañía del tercero no puede ser la propia organización.
                    companias: companias.filter((c) => String(c.id) !== String(rutOrganizacion)),
                    responsables,
                    causales,
                    colores,
                    usos
                });
                setModos(configuracion.modos);
                setModo(configuracion.modos.includes(MODO_RECUPERO) ? MODO_RECUPERO : (configuracion.modos[0] || MODO_RECUPERO));
            } catch (error) {
                console.error(error);
                setErrorCarga('No se pudieron cargar los datos del formulario. ' + (error.message || ''));
            } finally {
                setCargando(false);
            }
        };
        cargaCatalogos();
    }, [rutOrganizacion]);

    const payloadDe = (valores) => construyePayload(valores, usuario, catalogos.marcas, { modo, idCaso: edicion ? edicion.idCaso : null });

    // "Revisar": el servidor valida sin escribir nada. Si hay errores se muestran en los campos y se devuelven al formulario
    // (para que abra la pestaña que corresponda); si todo está bien, devuelve null y se abre la revisión.
    const handleRevisar = async (valores, { setErrors, setSubmitting }) => {
        setErrorGeneral('');
        setEnviando(true);
        try {
            const { errores } = await validarIngreso(payloadDe(valores));
            if (errores && errores.length > 0) {
                setErrors(erroresAMapa(errores));
                setErrorGeneral(errores.map((e) => e.mensaje).join(' '));
                return errores;
            }
            setRevision({ show: true, valores, error: '' });
            return null;
        } catch (error) {
            console.error(error);
            setErrorGeneral(error.message || 'No se pudo validar el ingreso.');
            return null;
        } finally {
            setEnviando(false);
            setSubmitting(false);
        }
    };

    // "Confirmar": crea el caso nuevo (y lo deja en la bandeja del abogado) o, en modo edición, actualiza el caso existente.
    const handleConfirmar = async () => {
        setGuardando(true);
        try {
            const datos = payloadDe(revision.valores);
            // La carta (opcional) viaja en base64 solo al confirmar; al revisar bastan sus datos (nombre, tipo y tamaño).
            if (datos.filename && revision.valores.cartaArchivo) {
                datos.base64data = await leeArchivoBase64(revision.valores.cartaArchivo);
                delete datos.fileSizeBytes;
            }
            const respuesta = edicion ? await actualizarIngreso(datos) : await crearIngreso(datos);
            setRevision(SIN_REVISION);
            setResultado(respuesta);
        } catch (error) {
            console.error(error);
            setRevision({ ...revision, error: error.message || (edicion ? 'No se pudo actualizar el caso.' : 'No se pudo crear el caso.') });
        } finally {
            setGuardando(false);
        }
    };

    // Vuelve a un ingreso nuevo con el formulario completamente limpio.
    const reiniciaFormulario = () => {
        setEdicion(null);
        setValoresForm(valoresIniciales());
        setErrorGeneral('');
        setClaveFormulario((k) => k + 1); // Remonta el formulario con los valores iniciales.
    };

    const handleOtro = () => {
        setResultado(null);
        reiniciaFormulario();
    };

    // Al salir de siniestro/patente el formulario avisa; si ya existe un caso del mismo flujo se pregunta si se quiere actualizar.
    // Es una ayuda: si la consulta falla, el servidor igual rechaza el duplicado al revisar y al confirmar.
    const handleVerificarExistente = async ({ siniestro, patente }) => {
        if (edicion || existente.show) return;
        try {
            const r = await existeCaso({ idOrganizacion: String(rutOrganizacion), idUsuario: String(rutUsuario), siniestro, patente, modo });
            if (r && r.existe) setExistente({ ...SIN_EXISTENTE, show: true, idCaso: r.idCaso });
        } catch (error) {
            console.error(error);
        }
    };

    // «Sí, actualizar»: se traen los datos del caso y el formulario se remonta precargado, en modo edición.
    const handleActualizar = async () => {
        setExistente((e) => ({ ...e, cargando: true, error: '' }));
        try {
            const caso = await obtenerCaso({ idOrganizacion: String(rutOrganizacion), idUsuario: String(rutUsuario), idCaso: existente.idCaso, modo });
            setEdicion({ idCaso: caso.idCaso, tieneCarta: !!caso.tieneCarta, folioCarta: caso.folioCarta });
            setValoresForm(valoresDeCaso(caso.valores));
            setErrorGeneral('');
            setClaveFormulario((k) => k + 1);
            setExistente(SIN_EXISTENTE);
        } catch (error) {
            console.error(error);
            setExistente((e) => ({ ...e, cargando: false, error: error.message || 'No se pudo cargar el caso.' }));
        }
    };

    // «Ver carta»: abre la carta adjunta en otra pestaña. La pestaña se abre antes de pedir la URL (si no, el navegador bloquea la
    // ventana emergente) y se le asigna la dirección cuando llega; si falla, se cierra y se informa.
    const handleVerCarta = async () => {
        const ventana = window.open('about:blank', '_blank');
        try {
            const url = await getUrlCarta({ idCaso: edicion.idCaso, idCompania: rutOrganizacion, idfolioCarga: edicion.folioCarta });
            if (ventana) ventana.location.href = url;
            else window.open(url, '_blank');
        } catch (error) {
            console.error(error);
            if (ventana) ventana.close();
            setErrorGeneral(error.message || 'No se pudo abrir la carta.');
        }
    };

    // «No»: el formulario se limpia por completo y es un ingreso nuevo.
    const handleNuevo = () => {
        setExistente(SIN_EXISTENTE);
        reiniciaFormulario();
    };

    const sinRol = catalogos && modos.length === 0;
    const defensa = modo === MODO_DEFENSA;

    return (
        <Container>
            <LoadingComponent show={cargando} />
            <Card className="mt-2 mb-4">
                <Card.Header as="h5">{defensa ? 'Ingreso manual — Cartas inter cía' : 'Ingreso manual — Recupero'}</Card.Header>
                <Card.Body>
                    {errorCarga ? (
                        <Alert variant="danger">
                            {errorCarga} <Button variant="link" className="p-0 align-baseline" onClick={() => window.location.reload()}>Reintentar</Button>
                        </Alert>
                    ) : null}
                    {sinRol ? (
                        <Alert variant="warning">Su usuario no tiene un rol habilitado para el ingreso manual de casos. Solicítelo a soporte.</Alert>
                    ) : null}
                    {catalogos && modos.length > 1 && !resultado ? (
                        <Form.Group className="mb-3">
                            <Form.Label className="me-3"><b>Tipo de ingreso</b></Form.Label>
                            {modos.map((m) => (
                                <Form.Check
                                    inline
                                    key={m}
                                    type="radio"
                                    id={`modo-${m}`}
                                    name="modo-ingreso"
                                    label={NOMBRE_MODO[m] || m}
                                    checked={modo === m}
                                    disabled={!!edicion}
                                    onChange={() => { setModo(m); reiniciaFormulario(); }}
                                />
                            ))}
                        </Form.Group>
                    ) : null}
                    {resultado ? <Resultado resultado={resultado} onOtro={handleOtro} /> : null}
                    {!resultado && catalogos && !sinRol ? (
                        <Formulario
                            key={`${modo}-${claveFormulario}`}
                            modo={modo}
                            catalogos={catalogos}
                            valores={valoresForm}
                            enviando={enviando}
                            errorGeneral={errorGeneral}
                            onRevisar={handleRevisar}
                            modoEdicion={edicion}
                            onVerificarExistente={handleVerificarExistente}
                            onCancelarEdicion={reiniciaFormulario}
                            onVerCarta={handleVerCarta}
                        />
                    ) : null}
                </Card.Body>
            </Card>
            {catalogos ? (
                <ResumenModal
                    show={revision.show}
                    valores={revision.valores}
                    catalogos={catalogos}
                    guardando={guardando}
                    error={revision.error}
                    modoEdicion={edicion}
                    modo={modo}
                    onVolver={() => setRevision(SIN_REVISION)}
                    onConfirmar={handleConfirmar}
                />
            ) : null}
            <ModalExistente
                show={existente.show}
                idCaso={existente.idCaso}
                cargando={existente.cargando}
                error={existente.error}
                onActualizar={handleActualizar}
                onNuevo={handleNuevo}
            />
        </Container>
    );
};

export default IngresoManualInterCia;
